import { type FC } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0Select } from '@factorialco/f0-react'
import { Sparkles } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useModuleAvailability } from 'modules/core/hooks/useModuleAvailability'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { type Location, LocationSelector } from 'modules/it_management/components/LocationSelector'
import { type Team, TeamSelector } from 'modules/it_management/components/TeamSelector'
import {
  type Workplace,
  WorkplaceSelector,
} from 'modules/it_management/components/WorkplaceSelector'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

export type AssignmentType = 'person' | 'workplace' | 'team' | 'space' | 'none'
export type DeviceStatus = 'in_stock' | 'maintenance' | 'retired'

export interface AssignmentFormData {
  assignmentType: AssignmentType
  selectedEmployeeId?: string
  selectedWorkplace?: Workplace | null
  selectedTeam?: Team | null
  selectedLocation?: Location | null
  deviceStatus?: DeviceStatus
}

interface AssignmentFormProps {
  value: AssignmentFormData
  onChange: (data: AssignmentFormData) => void
  showOnlyInStock?: boolean
}

export const AssignmentForm: FC<AssignmentFormProps> = ({
  value,
  onChange,
  showOnlyInStock = false,
}) => {
  const { company } = useCurrentSession()
  const { pathname } = useLocation()

  const { moduleAvailability: spaceControlAvailability } = useModuleAvailability(
    AvailableModules.space_control,
    company.id
  )

  const hasSpaceControl = spaceControlAvailability.available

  const handleAssignmentTypeChange = (newType: AssignmentType) => {
    onChange({
      assignmentType: newType,
      selectedEmployeeId: undefined,
      selectedWorkplace: null,
      selectedTeam: null,
      selectedLocation: null,
      deviceStatus: newType === 'none' ? 'in_stock' : undefined,
    })
  }

  const handleEmployeeChange = (employeeId: string | undefined) => {
    onChange({
      ...value,
      selectedEmployeeId: employeeId,
    })
  }

  const handleWorkplaceChange = (workplace: Workplace | null) => {
    onChange({
      ...value,
      selectedWorkplace: workplace,
    })
  }

  const handleTeamChange = (team: Team | null) => {
    onChange({
      ...value,
      selectedTeam: team,
    })
  }

  const handleLocationChange = (location: Location | null) => {
    onChange({
      ...value,
      selectedLocation: location,
    })
  }

  const handleDeviceStatusChange = (status: DeviceStatus) => {
    onChange({
      ...value,
      deviceStatus: status,
    })
  }

  const assignmentOptions: Array<{ label: string; value: AssignmentType; tag?: string }> = [
    {
      label: i18n.t('it_management.assets.assign_owner_modal.assign_type.person'),
      value: 'person',
    },
    {
      label: i18n.t('it_management.assets.assign_owner_modal.assign_type.workplace'),
      value: 'workplace',
    },
    {
      label: i18n.t('it_management.assets.assign_owner_modal.assign_type.team'),
      value: 'team',
    },
    {
      label: i18n.t('it_management.assets.assign_owner_modal.assign_type.space'),
      value: 'space',
      ...(hasSpaceControl ? {} : { tag: i18n.t('it_management.assignment_form.upgrade') }),
    },
    {
      label: i18n.t('it_management.assets.assign_owner_modal.assign_type.none'),
      value: 'none',
    },
  ]

  return (
    <Box gap="s16">
      <Box gap="s8">
        <F0Select<AssignmentType>
          label={i18n.t('it_management.assets.assign_owner_modal.assign_to_label')}
          value={value.assignmentType}
          onChange={handleAssignmentTypeChange}
          options={assignmentOptions}
          size="md"
        />
      </Box>

      {value.assignmentType === 'person' && (
        <Box gap="s8">
          <EmployeeSelectorV2
            value={value.selectedEmployeeId}
            onChange={handleEmployeeChange}
            options={{
              hideLabel: true,
            }}
            mandatory
          />
        </Box>
      )}

      {value.assignmentType === 'workplace' && (
        <WorkplaceSelector
          value={value.selectedWorkplace}
          onSelect={handleWorkplaceChange}
          emptyLabel={i18n.t('it_management.assignment_step.empty_label')}
        />
      )}

      {value.assignmentType === 'team' && (
        <TeamSelector
          value={value.selectedTeam}
          onSelect={handleTeamChange}
          emptyLabel={i18n.t('it_management.assignment_step.empty_label')}
        />
      )}

      {value.assignmentType === 'space' && (
        <>
          {hasSpaceControl ? (
            <LocationSelector
              value={value.selectedLocation}
              onSelect={handleLocationChange}
              emptyLabel={i18n.t('it_management.assignment_step.empty_label')}
            />
          ) : (
            <Box
              borderRadius={{ all: 'abs012' }}
              paddingX="s8"
              paddingY="s8"
              flexDirection="row"
              justifyContent="spaceBetween"
              style={{
                backgroundColor: 'rgb(245, 165, 28, 0.05)',
              }}
              alignItems="center"
            >
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Box
                  style={{
                    backgroundColor: 'rgb(245, 165, 28, 0.1)',
                  }}
                  padding="s4"
                  borderRadius={{
                    all: 'abs012',
                  }}
                >
                  <F0Icon color="promote" icon={Sparkles} size="md" fontWeight="bold" />
                </Box>
                <Text.Normal size="200" color="black" weight="medium">
                  {i18n.t('it_management.assignment_form.spaces_upgrade_message')}
                </Text.Normal>
              </Box>
              <UpsellingKit.UpsellButton
                moduleRequested={AvailableModules.space_control}
                requestSource="it_inventory_assignment_form"
                requestReason={ValidReasons.MissingAddon}
                pageSource={pathname}
                label={i18n.t('it_management.assignment_form.more_info')}
                size="md"
                variant="outlinePromote"
                showModuleModal
              />
            </Box>
          )}
        </>
      )}

      {value.assignmentType === 'none' && (
        <Box gap="s16">
          <Text.Normal size="200" color="grey800" weight="medium">
            {i18n.t('it_management.assets.assign_owner_modal.move_device_to')}
          </Text.Normal>
          <Box gap="s8" paddingLeft="s8">
            <RadioButton
              value="in_stock"
              checked={value.deviceStatus === 'in_stock'}
              onChange={() => handleDeviceStatusChange('in_stock')}
              label={i18n.t('it_management.assets.assign_owner_modal.device_status.in_stock')}
            />

            {!showOnlyInStock && (
              <>
                <RadioButton
                  value="maintenance"
                  checked={value.deviceStatus === 'maintenance'}
                  onChange={() => handleDeviceStatusChange('maintenance')}
                  label={i18n.t(
                    'it_management.assets.assign_owner_modal.device_status.maintenance'
                  )}
                />

                <RadioButton
                  value="retired"
                  checked={value.deviceStatus === 'retired'}
                  onChange={() => handleDeviceStatusChange('retired')}
                  label={i18n.t('it_management.assets.assign_owner_modal.device_status.retired')}
                />
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
